// RecommendationsScreen — director's SP2 funnel-bottleneck recommendations,
// with SP3 «Применить»: actionable cards (slow_stage / stuck_deals) open a
// confirm sheet that writes a standing stage_tasks rule; low_conversion cards
// deep-link to the AI-logic stage editor. When auto-execute is off, the sheet
// states the rule is saved but dormant until automation is enabled.
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { applyRecommendation, fetchRecommendations } from '../../api/recommendations'
import type { ApplyResult, RecommendationCard } from '../../api/recommendations'
import { Button, EmptyState, Icon, ScreenSkeleton, t, useToast } from '../../design'

const ACTIONABLE = new Set(['slow_stage', 'stuck_deals'])

function kindIcon(kind: RecommendationCard['kind']) {
  if (kind === 'slow_stage') return <Icon.Clock size={16} sw={2} />
  if (kind === 'low_conversion') return <Icon.PieChart size={16} sw={2} />
  return <Icon.AlertTri size={16} sw={2} />
}

function actionDay(c: RecommendationCard): number {
  const d = (c.suggested_action as { day?: number })?.day
  return typeof d === 'number' && d > 0 ? d : 1
}

function ConfirmApplySheet({
  card, pending, result, onConfirm, onClose, onOpenSettings,
}: {
  card: RecommendationCard
  pending: boolean
  result: ApplyResult | null
  onConfirm: () => void
  onClose: () => void
  onOpenSettings: () => void
}) {
  const autoOff = result != null && !result.auto_execute_moves
  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        {autoOff ? (
          <>
            <div className="t-h1">{t('reco.applied')}</div>
            <div className="t-body-s" style={{ marginTop: 8 }}>{t('reco.autoOffNote')}</div>
            <div className="row gap-2" style={{ marginTop: 16 }}>
              <Button variant="secondary" onClick={onOpenSettings}>{t('reco.openSettings')}</Button>
              <Button onClick={onClose}>{t('reco.done')}</Button>
            </div>
          </>
        ) : (
          <>
            <div className="t-h1">{t('reco.confirmTitle', { stage: card.stage_name })}</div>
            <div className="t-body-s" style={{ marginTop: 8 }}>
              {t('reco.confirmBody', { day: String(actionDay(card)) })}
            </div>
            <div className="row gap-2" style={{ marginTop: 16 }}>
              <Button variant="secondary" onClick={onClose} disabled={pending}>{t('reco.cancel')}</Button>
              <Button onClick={onConfirm} disabled={pending}>{t('reco.confirmCta')}</Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function Card({
  c, applied, onApply, onOpenStage,
}: {
  c: RecommendationCard
  applied: boolean
  onApply: (c: RecommendationCard) => void
  onOpenStage: () => void
}) {
  const actionable = ACTIONABLE.has(c.kind)
  return (
    <div className="card card-16">
      <div className="row middle gap-2" style={{ marginBottom: 6, color: 'var(--text-3)' }}>
        {kindIcon(c.kind)}
        <span className="t-caption" style={{ fontWeight: 600 }}>{t(`reco.kind.${c.kind}`)}</span>
        <span className="t-body-s flex-1" style={{ textAlign: 'right', fontWeight: 600, color: 'var(--text-1)' }}>
          {c.stage_name}
        </span>
      </div>
      <div className="t-body-s" style={{ marginBottom: 10 }}>{c.diagnosis_ru}</div>
      <div className="card" style={{ background: 'var(--brand-100)', padding: '10px 12px', marginBottom: 10 }}>
        <div className="t-caption-s" style={{ color: 'var(--brand-900)', fontWeight: 700, marginBottom: 2 }}>
          {t('reco.recommend')}
        </div>
        <div className="t-caption" style={{ color: 'var(--brand-900)' }}>{c.recommendation_ru}</div>
      </div>
      {actionable ? (
        applied ? (
          <button className="btn btn-ghost btn-sm" disabled
                  style={{ opacity: 0.7, cursor: 'default', color: 'var(--text-3)' }}>
            ✓ {t('reco.applied')}
          </button>
        ) : (
          <Button variant="secondary" onClick={() => onApply(c)}>{t('reco.apply')}</Button>
        )
      ) : (
        <Button variant="secondary" icon={<Icon.ArrowRight size={16} sw={2} />} onClick={onOpenStage}>
          {t('reco.openStage')}
        </Button>
      )}
    </div>
  )
}

export function RecommendationsScreen() {
  const navigate = useNavigate()
  const toast = useToast()
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['recommendations'],
    queryFn: fetchRecommendations,
  })
  const [sheetCard, setSheetCard] = useState<RecommendationCard | null>(null)
  const [result, setResult] = useState<ApplyResult | null>(null)
  const [appliedSet, setAppliedSet] = useState<Set<string>>(new Set())

  const mutation = useMutation({
    mutationFn: (c: RecommendationCard) => applyRecommendation(c),
    onSuccess: (res, c) => {
      setAppliedSet((s) => new Set(s).add(c.stage_ext))
      qc.invalidateQueries({ queryKey: ['recommendations'] })
      if (res.auto_execute_moves) {
        toast(t('reco.appliedToast'))
        setSheetCard(null)
      } else {
        setResult(res)            // keep sheet open to show the dormant-rule note
      }
    },
    onError: () => toast(t('reco.applyErr')),
  })

  const openSheet = (c: RecommendationCard) => { setResult(null); setSheetCard(c) }
  const closeSheet = () => { setSheetCard(null); setResult(null) }

  if (isLoading) return <ScreenSkeleton kind="list" />
  if (isError) {
    return (
      <div className="scroll-y" style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', gap: 14, padding: 24 }}>
        <Icon.CloudOff size={30} sw={2} style={{ color: 'var(--text-3)' }} />
        <div className="t-h2">{t('reco.loadErr')}</div>
        <Button variant="secondary" icon={<Icon.Refresh size={16} sw={2} />} onClick={() => refetch()}>{t('btn.retry')}</Button>
      </div>
    )
  }

  const cards = data?.cards ?? []
  const insufficient = data?.data_state !== 'ok' || cards.length === 0

  return (
    <div className="scroll-y" style={{ flex: 1, padding: '12px 16px 24px' }}>
      <div className="t-h1" style={{ marginBottom: 12 }}>{t('reco.title')}</div>
      {insufficient ? (
        <EmptyState icon={<Icon.PieChart size={28} />} title={t('reco.insufficient')} hint={t('reco.insufficientHint')} />
      ) : (
        <div className="col" style={{ gap: 10 }}>
          {cards.map((c) => (
            <Card
              key={`${c.kind}:${c.stage_ext}`}
              c={c}
              applied={Boolean(c.applied) || appliedSet.has(c.stage_ext)}
              onApply={openSheet}
              onOpenStage={() => navigate('/ai-settings')}
            />
          ))}
        </div>
      )}
      {sheetCard && (
        <ConfirmApplySheet
          card={sheetCard}
          pending={mutation.isPending}
          result={result}
          onConfirm={() => mutation.mutate(sheetCard)}
          onClose={closeSheet}
          onOpenSettings={() => { closeSheet(); navigate('/ai-settings') }}
        />
      )}
    </div>
  )
}
